import { wrapApiCall } from '@/lib/axios'
import axios, { type AxiosInstance } from 'axios'

export interface EmailChangeRequestResult {
  success: boolean
  newEmail: string
  expiresAt: string
}

export interface VerifyEmailChangeResult {
  success: boolean
  newEmail: string
  oldEmail: string | null
}

// Authenticated endpoints — go through the shared axios client so
// the usual tenant/auth/branch headers apply.
export const EmailChangeService = (client: AxiosInstance) => {
  return {
    /**
     * Ask the backend to start a verified email change for a staff member (or
     * yourself — "my profile" is your own staff record). The backend requires
     * a fresh Firebase `auth_time`, so the caller must re-authenticate with
     * their OWN password (Firebase `reauthenticateWithCredential`) right
     * before calling this.
     */
    async requestEmailChange(
      userId: string | number,
      newEmail: string
    ): Promise<EmailChangeRequestResult> {
      const { data } = await wrapApiCall(() =>
        client.post<EmailChangeRequestResult>(`/email-change/users/${userId}/request`, {
          newEmail,
        })
      )
      return data
    },
    async checkEmailAvailability(email: string): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.get<{ available: boolean }>('/email-change/availability', {
          params: { email },
        })
      )
      return !!data?.available
    },
  }
}

// Public endpoints — called from pages that may have no session (the verify
// page opened from the emailed link, the login screen). A bare axios call is
// used on purpose: the shared client's interceptor would overwrite
// x-tenant-id from localStorage, but here the tenant comes from the link.
const publicClient = axios.create({ baseURL: import.meta.env.VITE_API_URL as string | undefined })

export const verifyEmailChange = async (
  token: string,
  tenantId: string
): Promise<VerifyEmailChangeResult> => {
  const { data } = await publicClient.post<VerifyEmailChangeResult>(
    '/email-change/verify',
    { token },
    { headers: { 'x-tenant-id': tenantId } }
  )
  return data
}

export const getEmailChangeStatus = async (
  email: string,
  tenantId: string
): Promise<{ detached: boolean }> => {
  const { data } = await publicClient.get<{ detached: boolean }>('/email-change/status', {
    params: { email },
    headers: { 'x-tenant-id': tenantId },
  })
  return data
}
